import { useZero } from "@rocicorp/zero/react";
import { useState } from "react";
import {
  EXECUTION_RETENTION_SECONDS_PER_DAY,
  MAX_EXECUTION_RETENTION_SECONDS,
  MIN_EXECUTION_RETENTION_SECONDS,
} from "../../shared/executionRetention";
import { sharedMutators } from "../../shared/mutators";
import { queries } from "../../shared/queries";
import { useQuery } from "../hooks/useQuery";
import { Button } from "./Button";
import { Icon } from "./Icon";
import { Input } from "./Input";
import { Modal } from "./Modal";
import {
  MODAL_CARD_SHELL_STYLE,
  ModalContent,
  ModalFooter,
  ModalHead,
  ModalRow,
  ModalRows,
  ModalRowText,
  STEPPER_INPUT_STYLE,
} from "./ModalCard";
import { Stepper, StepButton } from "./Stepper";
import { Text } from "./Text";
import { WavyLine } from "./WavyLine";

type Props = {
  workflowId: string;
  onClose: () => void;
};

export function WorkflowSettings({ workflowId, onClose }: Props) {
  const [workflow, workflowQuery] = useQuery(
    queries.loadWorkflow({ workflowId })
  );

  if (workflowQuery.type !== "complete") {
    return (
      <Modal
        open
        onClose={onClose}
        style={{
          ...MODAL_CARD_SHELL_STYLE,
          height: 320,
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <WavyLine />
      </Modal>
    );
  }

  if (!workflow) return null;

  return (
    <Modal open onClose={onClose} style={MODAL_CARD_SHELL_STYLE}>
      <RetentionForm
        workflowId={workflowId}
        retentionSeconds={workflow.retentionSeconds!}
        onClose={onClose}
      />
    </Modal>
  );
}

function RetentionForm({
  workflowId,
  retentionSeconds,
  onClose,
}: Props & { retentionSeconds: number }) {
  const z = useZero();
  const initialDays = retentionSeconds / EXECUTION_RETENTION_SECONDS_PER_DAY;
  const [days, setDays] = useState(String(initialDays));
  const parsedDays = Number(days);
  const minimumDays =
    MIN_EXECUTION_RETENTION_SECONDS / EXECUTION_RETENTION_SECONDS_PER_DAY;
  const maximumDays =
    MAX_EXECUTION_RETENTION_SECONDS / EXECUTION_RETENTION_SECONDS_PER_DAY;
  const valid =
    Number.isInteger(parsedDays) &&
    parsedDays >= minimumDays &&
    parsedDays <= maximumDays;
  const changed = parsedDays !== initialDays;

  const step = (delta: number) => {
    const base = Number.isFinite(parsedDays) ? parsedDays : minimumDays;
    setDays(String(Math.min(maximumDays, Math.max(minimumDays, base + delta))));
  };

  const setDaysFromInput = (raw: string) => {
    const digits = raw.replace(/\D/g, "");
    setDays(digits === "" ? "" : String(Math.min(maximumDays, Number(digits))));
  };

  const commitDays = () => {
    if (parsedDays < minimumDays) setDays(String(minimumDays));
  };

  const save = () => {
    if (!valid || !changed) return;
    z.mutate(
      sharedMutators.setWorkflowRetention({
        workflowId,
        retentionSeconds: parsedDays * EXECUTION_RETENTION_SECONDS_PER_DAY,
      })
    );
    onClose();
  };

  return (
    <ModalContent>
      <ModalHead>
        <Text variant="text-regular-md" color="var(--text-primary)">
          Execution retention
        </Text>
        <Text variant="text-regular" color="var(--text-secondary)">
          Choose how long completed runs, execution history, outputs, and
          run-scoped volume data are retained. Changes apply to future runs.
        </Text>
      </ModalHead>
      <ModalRows>
        <ModalRow>
          <ModalRowText>
            <Text variant="text-regular-md" color="var(--text-primary)">
              Retention period
            </Text>
            <Text variant="text-small" color="var(--text-secondary)">
              Between {minimumDays} and {maximumDays} days
            </Text>
          </ModalRowText>
          <Stepper>
            <StepButton
              data-no-pill
              aria-label="Decrease retention period"
              disabled={parsedDays <= minimumDays}
              onPress={() => step(-1)}
            >
              <Icon name="minus" size={16} color="var(--text-secondary)" />
            </StepButton>
            <Input
              inputMode="numeric"
              value={days}
              onChange={(event) => setDaysFromInput(event.target.value)}
              onBlur={commitDays}
              onKeyDown={(event) => {
                if (event.key === "Enter") save();
              }}
              style={STEPPER_INPUT_STYLE}
            />
            <StepButton
              data-no-pill
              aria-label="Increase retention period"
              disabled={parsedDays >= maximumDays}
              onPress={() => step(1)}
            >
              <Icon name="plus" size={16} color="var(--text-secondary)" />
            </StepButton>
          </Stepper>
        </ModalRow>
      </ModalRows>
      <ModalFooter>
        <Button size="l" variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button
          size="l"
          variant="primary"
          onClick={save}
          disabled={!valid || !changed}
        >
          Save
        </Button>
      </ModalFooter>
    </ModalContent>
  );
}
